import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import "bootstrap/dist/css/bootstrap.min.css";
import "../assets/css/style.css";

import { useLanguage } from "../context/LanguageContext";

// ============================================================
// API
// ============================================================

//const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5555";
const API_BASE_URL = "http://localhost:5555";
// ============================================================
// LOCATIONS
// ============================================================

const LOCATIONS = {
  30: "Airport Heraklion",
  29: "Airport Chania",
  28: "Airport Sitia",
  27: "Harbour Heraklion",
  26: "Harbour Chania",
  25: "Harbour Sitia",
  24: "Accommodation (apartment, villa, hotel etc.)",
};

// ============================================================
// FREE EXTRAS
// ============================================================

const FREE_EXTRAS = [
  {
    id: 15,
    name: "I want to pay the final payment by credit card",
    icon: "💳",
  },
  {
    id: 14,
    name: "I want to pay the final payment in cash",
    icon: "💶",
  },
  {
    id: 8,
    name: "Non smoking car",
    icon: "🚭",
  },
  {
    id: 12,
    name: "Additional driver",
    icon: "👨‍✈️",
  },
];

// ============================================================
// PAID EXTRAS
// ============================================================

const PAID_EXTRAS = [
  {
    id: 17,
    name: "Baby Seat ISOFIX",
    description: "(09-36 KG)",
    icon: "👶",
    price: 8,
  },
  {
    id: 2,
    name: "Baby Seat",
    description: "(baby till 10 kg)",
    icon: "👶",
    price: 4,
  },
  {
    id: 7,
    name: "Child Seat",
    description: "(10 - 18 kg.)",
    icon: "🧒",
    price: 4,
  },
  {
    id: 16,
    name: "Child Seat",
    description: "(18 - 22 kg.)",
    icon: "🧒",
    price: 4,
  },
  {
    id: 5,
    name: "Booster seat",
    description: "(child from 22 kg. and 5 years old.)",
    icon: "🪑",
    price: 2,
  },
];

// ============================================================
// COMPONENT
// ============================================================

function BookACarFour() {
  const navigate = useNavigate();

  const { translations } = useLanguage();

  // ==========================================================
  // STATE
  // ==========================================================

  const [bookingData, setBookingData] = useState(null);

  const [loading, setLoading] = useState(true);

  // Customer information
  const [customer, setCustomer] = useState({
    title: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    country: "",
    postcode: "",
    remarks: "",
  });

  // Payment method
  const [paymentMethod, setPaymentMethod] = useState("paynow");

  // Terms
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  // ==========================================================
  // TRANSLATION
  // ==========================================================

  const getTranslation = (key, fallback = "") => {
    const value = translations?.bookingengine?.[key];

    if (typeof value === "string" && value.trim() !== "") {
      return value;
    }

    if (typeof value === "number") {
      return String(value);
    }

    return fallback;
  };

  // ==========================================================
  // LOAD BOOKING STEP THREE
  // ==========================================================

  useEffect(() => {
    try {
      const storedData =
        sessionStorage.getItem("bookingStepThree");

      if (!storedData) {
        console.warn(
          "No bookingStepThree found in sessionStorage"
        );

        navigate("/bookacarthree");

        return;
      }

      const parsedData = JSON.parse(storedData);

      console.log(
        "BOOKING STEP 4 DATA LOADED:",
        parsedData
      );

      setBookingData(parsedData);
    } catch (error) {
      console.error(
        "Error loading booking step four:",
        error
      );

      navigate("/bookacarthree");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  // ==========================================================
  // CAR IMAGE
  // ==========================================================

  const getCarImage = (image) => {
    if (!image) {
      return "";
    }

    let imagePath = String(image).trim();

    if (!imagePath) {
      return "";
    }

    if (
      imagePath.startsWith("http://") ||
      imagePath.startsWith("https://")
    ) {
      return imagePath;
    }

    imagePath = imagePath.replace(/^\/+/, "");

    if (imagePath.startsWith("uploads/")) {
      return `${API_BASE_URL}/${imagePath}`;
    }

    if (imagePath.startsWith("images/")) {
      return `${API_BASE_URL}/uploads/${imagePath}`;
    }

    return `${API_BASE_URL}/uploads/cars/${imagePath}`;
  };

  // ==========================================================
  // FORMAT DATE
  // ==========================================================

  const formatDate = (date) => {
    if (!date) {
      return "";
    }

    return String(date);
  };

  // ==========================================================
  // TIME
  // ==========================================================

  const getTime = (hour, minute) => {
    if (
      hour === undefined ||
      hour === null ||
      hour === ""
    ) {
      return "";
    }

    return `${hour}:${String(
      minute ?? "00"
    ).padStart(2, "0")}`;
  };

  // ==========================================================
  // LOCATION
  // ==========================================================

  const getLocationName = (id) => {
    if (!id) {
      return "Not selected";
    }

    return (
      LOCATIONS[String(id)] ||
      id
    );
  };

  // ==========================================================
  // CUSTOMER INPUT
  // ==========================================================

  const handleCustomerChange = (event) => {
    const { name, value } = event.target;

    setCustomer((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  // ==========================================================
  // BOOKING VALUES
  // ==========================================================

  const car = bookingData?.car || {};

  const bookingCriteria =
    bookingData?.bookingCriteria || {};

  const rentalDays =
    Number(bookingData?.rentalDays || 1);

  const rentalPrice =
    Number(bookingData?.rentalPrice || 0);

  const pickupCharge =
    Number(bookingData?.pickupCharge || 0);

  const dropoffCharge =
    Number(bookingData?.dropoffCharge || 0);

  const oneWayCharge =
    Number(bookingData?.oneWayCharge || 0);

  const rentalTotal =
    Number(bookingData?.rentalTotal || 0);

  const paidExtrasTotal =
    Number(bookingData?.paidExtrasTotal || 0);

  const finalTotal =
    Number(bookingData?.finalTotal || 0);

  // ==========================================================
  // CAR DETAILS
  // ==========================================================

  const carName =
    car?.carmodel_name ||
    car?.name ||
    car?.carmodel ||
    "Selected car";

  const category =
    car?.cat_name ||
    car?.category ||
    "";

  const carImage = getCarImage(
    car?.carmodel_image ||
    car?.image
  );

  const passengers =
    car?.carmodel_passgrno ??
    car?.passengers ??
    0;

  const luggage =
    car?.carmodel_luggno ??
    car?.luggage ??
    0;

  const transmission =
    car?.transmission ||
    car?.gear ||
    "Manual";

  const airCondition =
    car?.airco ||
    car?.air_condition ||
    "Air-Co";

  const pricePerDay =
    Number(
      car?.pricePerDay ??
      car?.price_perday ??
      car?.price ??
      0
    );

  // ==========================================================
  // SELECTED FREE EXTRAS
  // ==========================================================

  const selectedFreeExtras =
    bookingData?.freeExtras || [];

  // ==========================================================
  // SELECTED PAID EXTRAS
  // ==========================================================

  const paidExtras =
    bookingData?.paidExtras || {};

  const activePaidExtras = useMemo(() => {
    return PAID_EXTRAS.filter(
      (item) =>
        Number(paidExtras[item.id] || 0) > 0
    ).map((item) => ({
      ...item,
      quantity: Number(
        paidExtras[item.id] || 0
      ),
    }));
  }, [paidExtras]);

  // ==========================================================
  // SUBMIT BOOKING
  // ==========================================================

//   const handleConfirmBooking = () => {
//     if (!customer.firstName.trim()) {
//       alert("Please enter your first name.");
//       return;
//     }

//     if (!customer.lastName.trim()) {
//       alert("Please enter your last name.");
//       return;
//     }

//     if (!customer.email.trim()) {
//       alert("Please enter your email.");
//       return;
//     }

//     if (!customer.phone.trim()) {
//       alert("Please enter your phone number.");
//       return;
//     }

//     if (!acceptedTerms) {
//       alert(
//         "Please accept the terms and conditions."
//       );
//       return;
//     }

//     const finalBooking = {
//       ...bookingData,

//       customer,

//       paymentMethod,

//       acceptedTerms,

//       confirmedAt:
//         new Date().toISOString(),
//     };

//     console.log(
//       "FINAL BOOKING DATA:",
//       finalBooking
//     );

//     sessionStorage.setItem(
//       "finalBooking",
//       JSON.stringify(finalBooking)
//     );

//     /*
//      * For now we store the booking.
//      *
//      * You can connect your booking/payment API
//      * here later.
//      */

//     alert(
//       "Booking information saved successfully."
//     );

//     // Change this later to your payment/confirmation page
//     navigate("/bookacarfour");
//   };
// const handleConfirmBooking = () => {
//   if (!customer.firstName.trim()) {
//     alert("Please enter your first name.");
//     return;
//   }

//   if (!customer.lastName.trim()) {
//     alert("Please enter your last name.");
//     return;
//   }

//   if (!customer.email.trim()) {
//     alert("Please enter your email.");
//     return;
//   }

//   if (!customer.phone.trim()) {
//     alert("Please enter your phone number.");
//     return;
//   }

//   if (!acceptedTerms) {
//     alert("Please accept the terms and conditions.");
//     return;
//   }

//   const finalBooking = {
//     ...bookingData,

//     customer,

//     paymentMethod,

//     acceptedTerms,

//     confirmedAt: new Date().toISOString(),
//   };

//   console.log("FINAL BOOKING DATA:", finalBooking);

//   // Save complete booking information
//   sessionStorage.setItem(
//     "finalBooking",
//     JSON.stringify(finalBooking)
//   );

//   // Go to Confirm Booking page
//   navigate("/confirmbooking");
// };

const handleConfirmBooking = async () => {

  if (!customer.firstName.trim()) {
    alert("Please enter your first name.");
    return;
  }

  if (!customer.lastName.trim()) {
    alert("Please enter your last name.");
    return;
  }

  if (!customer.email.trim()) {
    alert("Please enter your email.");
    return;
  }

  if (!customer.phone.trim()) {
    alert("Please enter your phone number.");
    return;
  }

  if (!acceptedTerms) {
    alert(
      "Please accept the terms and conditions."
    );
    return;
  }

  try {

    // ============================================
    // COMPLETE BOOKING OBJECT
    // ============================================

    const finalBooking = {

      bookingData,

      customer,

      paymentMethod,

      acceptedTerms,

      confirmedAt:
        new Date().toISOString()
    };

    console.log(
      "SENDING BOOKING:",
      finalBooking
    );

    // ============================================
    // INSERT INTO tbl_onlinebooking
    // ============================================

    const response =
      await fetch(
        `${API_BASE_URL}/api/bookings/create`,
        {
          method: "POST",

          headers: {
            "Content-Type":
              "application/json"
          },

        //   body: JSON.stringify({
        //     bookingData,
        //     customer,
        //     paymentMethod,
        //     acceptedTerms
        //   })

        body: JSON.stringify({
  // =========================
  // CUSTOMER
  // =========================
  online_title: customer.title,
  online_firstname: customer.firstName,
  online_lastname: customer.lastName,
  online_telephone: customer.phone,
  online_mobileno: customer.phone,
  online_gender: "",

  online_emailid: customer.email,
  online_postcode: customer.postcode,
  online_city: customer.city,
  online_country: customer.country,
  online_address: customer.address,

  // =========================
  // PICKUP / DROP
  // =========================
  online_pickdetails:
    getLocationName(
      bookingCriteria?.cmbPickLocation
    ),

  online_dropdetails:
    getLocationName(
      bookingCriteria?.cmbDropLocation
    ),

  online_pickcity:
    getLocationName(
      bookingCriteria?.cmbPickLocation
    ),

  online_returncity:
    getLocationName(
      bookingCriteria?.cmbDropLocation
    ),

  online_pickstation:
    bookingCriteria?.cmbPickLocation || "",

  online_returnstation:
    bookingCriteria?.cmbDropLocation || "",

  // =========================
  // DATE / TIME
  // =========================
  online_pickdate:
    bookingCriteria?.pickdate || "",

  online_returndate:
    bookingCriteria?.returndate || "",

  online_picktime:
    getTime(
      bookingCriteria?.cmbpickHour,
      bookingCriteria?.cmbpickmin
    ),

  online_returntime:
    getTime(
      bookingCriteria?.cmbreturnHour,
      bookingCriteria?.cmbreturnmin
    ),

  online_diffdate:
    rentalDays,

  online_diffhour: 0,

  // =========================
  // CAR
  // =========================
  online_carmodelid:
    car?.carmodel_id ||
    car?.car_id ||
    car?.id ||
    0,

  online_carcatid:
    car?.cat_id ||
    0,

  online_carprice:
    rentalPrice,

  online_offlineprice:
    rentalPrice,

  online_onlineprice:
    finalTotal,

  // =========================
  // CHARGES
  // =========================
  online_pickhourcharge: 0,

  online_drophourcharge: 0,

  online_locpickupcharge:
    pickupCharge,

  online_locdropoffcharge:
    dropoffCharge,

  online_picknightcharge: 0,

  online_dropnightcharge: 0,

  online_extrahrcharge: 0,

  // =========================
  // EXTRAS
  // =========================
  online_totaloptextras:
    paidExtrasTotal,

  online_totalinsurance: 0,

  online_onewayrentalcharges:
    oneWayCharge,

  // =========================
  // DRIVER / DISCOUNT
  // =========================
  online_driverage:
    bookingCriteria?.cmbdrvage || 0,

  online_discpercent: 0,

  online_disctype: 0,

  online_discamount: 0,

  // =========================
  // LANGUAGE / PAYMENT
  // =========================
  online_lang: 0,

  payment_type:
    paymentMethod === "paynow"
      ? 1
      : 0,

  booked_through: 1
})
        }
      );

    const result =
      await response.json();

    console.log(
      "CREATE BOOKING RESPONSE:",
      result
    );

    if (!response.ok || !result.success) {

      alert(
        result.message ||
        "Unable to create booking."
      );

      return;
    }

    // ============================================
    // SAVE BOOKING ID
    // ============================================

    const finalBookingWithId = {

      ...finalBooking,

      bookingid:
        result.bookingid,

      fullbooking_no:
        result.fullbooking_no
    };

    sessionStorage.setItem(
      "finalBooking",
      JSON.stringify(
        finalBookingWithId
      )
    );

    // ============================================
    // IMPORTANT
    // Go to CONFIRM BOOKING page
    // ============================================

    // navigate(
    //   `/confirmbooking?bookingid=${result.bookingid}`
    // );

    // With this:
navigate("/confirmbooking", {
  state: { bookingid: result.bookingid },
});

  } catch (error) {

    // console.error(
    //   "Create booking error:",
    //   error
    // );
console.error("=================================");
  console.error("CREATE BOOKING ERROR");
  console.error("=================================");
  console.error("Error:", error);
  console.error("Message:", error.message);
    alert(
      "Server error while creating booking."
    );
  }
};
  // ==========================================================
  // PREVIOUS
  // ==========================================================

  const handlePrevious = () => {
    navigate("/bookacarthree");
  };

  // ==========================================================
  // LOADING
  // ==========================================================

  if (loading) {
    return (
      <div
        className="text-center"
        style={{
          padding: "100px 20px",
        }}
      >
        <h4>
          {getTranslation(
            "loading",
            "Loading booking details..."
          )}
        </h4>
      </div>
    );
  }

  // ==========================================================
  // NO DATA
  // ==========================================================

  if (!bookingData) {
    return null;
  }

  // ==========================================================
  // UI
  // ==========================================================

  return (
    <div className="p20">

      <div className="container container2 p0">

        {/* ==================================================
            SIDEBAR
        ================================================== */}

        <div
          className="col-md-3 sidebar"
          style={{
            float: "left",
          }}
        >

          <div className="booking-wrapper">

            {/* ==================================================
                YOUR BOOKING
            ================================================== */}

            <div className="booking-card">

              <div className="card-header">

                <h3>
                  {getTranslation(
                    "yourBooking",
                    "Your booking"
                  )}
                </h3>

                <label
                  className="search-btn"
                  onClick={() =>
                    navigate("/bookacar")
                  }
                  style={{
                    cursor: "pointer",
                  }}
                >
                  {getTranslation(
                    "modify",
                    "Modify"
                  )}

                  {" "}

                  <i className="fa-solid fa-arrow-down"></i>
                </label>

              </div>

              {/* PICKUP */}

              <div className="booking-item">

                <div className="icon-area">

                  <div className="icon">
                    <i className="fa-solid fa-location-dot"></i>
                  </div>

                  <div className="line"></div>

                </div>

                <div className="booking-content">

                  <h4>
                    {getLocationName(
                      bookingCriteria?.cmbPickLocation
                    )}
                  </h4>

                  <p>
                    {formatDate(
                      bookingCriteria?.pickdate
                    )}

                    {" @ "}

                    {getTime(
                      bookingCriteria?.cmbpickHour,
                      bookingCriteria?.cmbpickmin
                    )}
                  </p>

                </div>

              </div>

              {/* RETURN */}

              <div className="booking-item">

                <div className="icon-area">

                  <div className="icon">
                    <i className="fa-solid fa-location-dot"></i>
                  </div>

                </div>

                <div className="booking-content">

                  <h4>
                    {getLocationName(
                      bookingCriteria?.cmbDropLocation
                    )}
                  </h4>

                  <p>
                    {formatDate(
                      bookingCriteria?.returndate
                    )}

                    {" @ "}

                    {getTime(
                      bookingCriteria?.cmbreturnHour,
                      bookingCriteria?.cmbreturnmin
                    )}
                  </p>

                </div>

              </div>

            </div>

            {/* ==================================================
                YOUR CAR
            ================================================== */}

            <div className="your-car">
              {getTranslation(
                "yourCar",
                "Your Car"
              )}
            </div>

            <div className="car-box price-box">

              <h2
                className="car-title"
                style={{
                  textAlign: "center",
                  fontSize: "20px",
                  lineHeight: "19px",
                }}
              >

                {carName}

                <br />

                <span
                  style={{
                    fontSize: "14px",
                    fontWeight: 500,
                  }}
                >
                  {category || "or similar"}
                </span>

              </h2>

              {carImage && (
                <img
                  src={carImage}
                  alt={carName}
                  className="car-img"
                  onError={(event) => {
                    event.currentTarget.style.display =
                      "none";
                  }}
                />
              )}

              <div
                className="specs-row"
                style={{
                  textAlign: "center",
                  margin: "0 14px",
                }}
              >

                <span className="spec-tag">
                  👤 {passengers}
                </span>

                <span className="spec-tag">
                  🧳 {luggage}
                </span>

                <span className="spec-tag">
                  ❄️ {airCondition}
                </span>

                <span className="spec-tag">
                  ⚙️ {transmission}
                </span>

              </div>

              <p
                className="big-size-text1"
                style={{
                  textAlign: "center",
                  marginTop: "15px",
                }}
              >
                Car rental fee
              </p>

              <p
                className="big-size-text"
                style={{
                  textAlign: "center",
                }}
              >
                € {rentalPrice.toFixed(2)}
              </p>

            </div>

          </div>

          <div className="clearfix"></div>

        </div>

        {/* ==================================================
            MAIN CONTENT
        ================================================== */}

        <div
          className="col-md-9 main-content"
          style={{
            float: "left",
          }}
        >

          {/* ==================================================
              STEP 4 TITLE
          ================================================== */}

          <div className="rate">

            {getTranslation(
              "bookingDetails",
              "Booking Details"
            )}

          </div>

          {/* ==================================================
              CUSTOMER INFORMATION
          ================================================== */}

          <div
            className="car-box price-box"
            style={{
              padding: "20px",
              marginBottom: "20px",
            }}
          >

            <h3
              style={{
                marginBottom: "20px",
              }}
            >
              {getTranslation(
                "customerInformation",
                "Customer Information"
              )}
            </h3>

            <div className="row">

              {/* TITLE */}

              <div className="col-md-3 mb-3">

                <label className="form-label">
                  {getTranslation(
                    "title",
                    "Title"
                  )}
                </label>

                <select
                  className="form-control"
                  name="title"
                  value={customer.title}
                  onChange={
                    handleCustomerChange
                  }
                >
                  <option value="">
                    Select
                  </option>

                  <option value="Mr">
                    Mr
                  </option>

                  <option value="Mrs">
                    Mrs
                  </option>

                  <option value="Ms">
                    Ms
                  </option>

                  <option value="Dr">
                    Dr
                  </option>
                </select>

              </div>

              {/* FIRST NAME */}

              <div className="col-md-4 mb-3">

                <label className="form-label">
                  {getTranslation(
                    "firstName",
                    "First Name"
                  )}
                  {" *"}
                </label>

                <input
                  type="text"
                  className="form-control"
                  name="firstName"
                  value={customer.firstName}
                  onChange={
                    handleCustomerChange
                  }
                  placeholder="First name"
                />

              </div>

              {/* LAST NAME */}

              <div className="col-md-5 mb-3">

                <label className="form-label">
                  {getTranslation(
                    "lastName",
                    "Last Name"
                  )}
                  {" *"}
                </label>

                <input
                  type="text"
                  className="form-control"
                  name="lastName"
                  value={customer.lastName}
                  onChange={
                    handleCustomerChange
                  }
                  placeholder="Last name"
                />

              </div>

              {/* EMAIL */}

              <div className="col-md-6 mb-3">

                <label className="form-label">
                  {getTranslation(
                    "email",
                    "Email"
                  )}
                  {" *"}
                </label>

                <input
                  type="email"
                  className="form-control"
                  name="email"
                  value={customer.email}
                  onChange={
                    handleCustomerChange
                  }
                  placeholder="Email address"
                />

              </div>

              {/* PHONE */}

              <div className="col-md-6 mb-3">

                <label className="form-label">
                  {getTranslation(
                    "phone",
                    "Phone"
                  )}
                  {" *"}
                </label>

                <input
                  type="text"
                  className="form-control"
                  name="phone"
                  value={customer.phone}
                  onChange={
                    handleCustomerChange
                  }
                  placeholder="Phone number"
                />

              </div>

              {/* ADDRESS */}

              <div className="col-md-12 mb-3">

                <label className="form-label">
                  {getTranslation(
                    "address",
                    "Address"
                  )}
                </label>

                <input
                  type="text"
                  className="form-control"
                  name="address"
                  value={customer.address}
                  onChange={
                    handleCustomerChange
                  }
                  placeholder="Address"
                />

              </div>

              {/* CITY */}

              <div className="col-md-4 mb-3">

                <label className="form-label">
                  {getTranslation(
                    "city",
                    "City"
                  )}
                </label>

                <input
                  type="text"
                  className="form-control"
                  name="city"
                  value={customer.city}
                  onChange={
                    handleCustomerChange
                  }
                  placeholder="City"
                />

              </div>

              {/* COUNTRY */}

              <div className="col-md-4 mb-3">

                <label className="form-label">
                  {getTranslation(
                    "country",
                    "Country"
                  )}
                </label>

                <input
                  type="text"
                  className="form-control"
                  name="country"
                  value={customer.country}
                  onChange={
                    handleCustomerChange
                  }
                  placeholder="Country"
                />

              </div>

              {/* POSTCODE */}

              <div className="col-md-4 mb-3">

                <label className="form-label">
                  {getTranslation(
                    "postcode",
                    "Postcode"
                  )}
                </label>

                <input
                  type="text"
                  className="form-control"
                  name="postcode"
                  value={customer.postcode}
                  onChange={
                    handleCustomerChange
                  }
                  placeholder="Postcode"
                />

              </div>

              {/* REMARKS */}

              <div className="col-md-12 mb-3">

                <label className="form-label">
                  {getTranslation(
                    "remarks",
                    "Remarks"
                  )}
                </label>

                <textarea
                  className="form-control"
                  rows="4"
                  name="remarks"
                  value={customer.remarks}
                  onChange={
                    handleCustomerChange
                  }
                  placeholder="Additional remarks"
                />

              </div>

            </div>

          </div>

          {/* ==================================================
              BOOKING SUMMARY
          ================================================== */}

          <div className="rate">

            {getTranslation(
              "bookingSummary",
              "Booking Summary"
            )}

          </div>

          <div
            className="price-box"
            style={{
              padding: 0,
              marginBottom: "20px",
            }}
          >

            <div className="rate-box">

              <table
                className="table word-break"
                width="100%"
                cellSpacing="2"
                cellPadding="2"
                border="0"
                style={{
                  margin: 0,
                }}
              >

                <thead>

                  <tr>

                    <td
                      width="49%"
                      className="tbltxt"
                    >
                      Quote
                    </td>

                    <td
                      width="28%"
                      align="center"
                      className="tbltxt"
                    >
                      Calculation
                    </td>

                    <td
                      width="23%"
                      align="right"
                      className="tbltxt"
                    >
                      Total
                    </td>

                  </tr>

                </thead>

                <tbody>

                  {/* RENTAL */}

                  <tr>

                    <td className="priceh2">
                      Rental price
                    </td>

                    <td
                      align="center"
                      className="priceh2"
                      style={{
                        color: "#0a9311",
                      }}
                    >
                      <strong>
                        {rentalDays} x{" "}
                        {pricePerDay.toFixed(2)} €
                      </strong>
                    </td>

                    <td
                      align="right"
                      className="priceh2"
                      style={{
                        color: "#0a9311",
                      }}
                    >
                      <strong>
                        {rentalPrice.toFixed(2)} €
                      </strong>
                    </td>

                  </tr>

                  {/* PICKUP */}

                  <tr>

                    <td
                      colSpan="2"
                      className="tbltxtnew"
                    >
                      Pick up charge
                    </td>

                    <td
                      align="right"
                      className="tbltxtnew"
                    >
                      {pickupCharge.toFixed(2)} €
                    </td>

                  </tr>

                  {/* DROP */}

                  <tr>

                    <td
                      colSpan="2"
                      className="tbltxtnew"
                    >
                      Drop off charge
                    </td>

                    <td
                      align="right"
                      className="tbltxtnew"
                    >
                      {dropoffCharge.toFixed(2)} €
                    </td>

                  </tr>

                  {/* ONE WAY */}

                  <tr>

                    <td
                      colSpan="2"
                      className="tbltxtnew"
                    >
                      One way rental charges
                    </td>

                    <td
                      align="right"
                      className="tbltxtnew"
                    >
                      {oneWayCharge.toFixed(2)} €
                    </td>

                  </tr>

                  {/* RENTAL TOTAL */}

                  <tr>

                    <td
                      colSpan="2"
                      className="priceh2"
                    >
                      <strong>
                        Total rental price
                      </strong>
                    </td>

                    <td
                      align="right"
                      className="priceh2"
                      style={{
                        color: "#8a74dd",
                      }}
                    >
                      <strong>
                        {rentalTotal.toFixed(2)} €
                      </strong>
                    </td>

                  </tr>

                  {/* EXTRAS */}

                  <tr>

                    <td
                      colSpan="2"
                      className="priceh2"
                    >
                      <strong>
                        Total extra charges
                      </strong>
                    </td>

                    <td
                      align="right"
                      style={{
                        color: "indianred",
                      }}
                    >
                      <strong>
                        {paidExtrasTotal.toFixed(2)} €
                      </strong>
                    </td>

                  </tr>

                  {/* FINAL */}

                  <tr
                    style={{
                      background: "#edf7ec",
                    }}
                  >

                    <td
                      colSpan="2"
                      className="pricehl"
                    >
                      <strong>
                        Final total rental price
                      </strong>
                    </td>

                    <td align="right">

                      <strong className="estimatedtotalprice">
                        {finalTotal.toFixed(2)} €
                      </strong>

                    </td>

                  </tr>

                </tbody>

              </table>

            </div>

            <div className="clearfix"></div>

          </div>

          {/* ==================================================
              SELECTED EXTRAS
          ================================================== */}

          <div className="optiona-extras">

            {getTranslation(
              "selectedExtras",
              "Selected Extras"
            )}

            <br />

            <span>
              {getTranslation(
                "selectedExtrasDescription",
                "Your selected optional extras"
              )}
            </span>

          </div>

          <div
            className="car-box price-box"
            style={{
              padding: 0,
              marginBottom: "20px",
            }}
          >

            <table
              className="table word-break"
              width="100%"
              cellSpacing="2"
              cellPadding="2"
              border="0"
            >

              <tbody>

                {/* FREE EXTRAS */}

                {selectedFreeExtras.length > 0 && (
                  <>
                    {FREE_EXTRAS
                      .filter((item) =>
                        selectedFreeExtras.includes(
                          item.id
                        )
                      )
                      .map((item) => (

                        <tr key={item.id}>

                          <td className="bookingtbltxt">

                            {item.icon}{" "}

                            {item.name}

                          </td>

                          <td
                            align="right"
                            className="bookingtbltxt"
                          >
                            FREE
                          </td>

                        </tr>

                      ))}
                  </>
                )}

                {/* PAID EXTRAS */}

                {activePaidExtras.map(
                  (item) => {

                    const total =
                      item.price *
                      rentalDays *
                      item.quantity;

                    return (
                      <tr key={item.id}>

                        <td className="bookingtbltxt">

                          {item.icon}{" "}

                          {item.name}{" "}

                          <span
                            style={{
                              fontSize: "13px",
                            }}
                          >
                            {item.description}
                          </span>

                          {" × "}

                          {item.quantity}

                        </td>

                        <td
                          align="right"
                          className="bookingtbltxt"
                        >
                          {total.toFixed(2)} €
                        </td>

                      </tr>
                    );
                  }
                )}

                {selectedFreeExtras.length === 0 &&
                  activePaidExtras.length === 0 && (

                    <tr>

                      <td
                        colSpan="2"
                        className="bookingtbltxt"
                      >
                        No optional extras selected.
                      </td>

                    </tr>

                  )}

              </tbody>

            </table>

          </div>

          {/* ==================================================
              PAYMENT
          ================================================== */}

          <div className="rate">

            {getTranslation(
              "payment",
              "Payment"
            )}

          </div>

          <div
            className="car-box price-box"
            style={{
              padding: "20px",
              marginBottom: "20px",
            }}
          >

            <h4>
              {getTranslation(
                "selectPaymentMethod",
                "Select Payment Method"
              )}
            </h4>

            <div
              style={{
                marginTop: "15px",
              }}
            >

              {/* PAY NOW */}

              <label
                style={{
                  display: "block",
                  marginBottom: "12px",
                  cursor: "pointer",
                }}
              >

                <input
                  type="radio"
                  name="paymentMethod"
                  value="paynow"
                  checked={
                    paymentMethod ===
                    "paynow"
                  }
                  onChange={(event) =>
                    setPaymentMethod(
                      event.target.value
                    )
                  }
                  style={{
                    marginRight: "10px",
                  }}
                />

                {getTranslation(
                  "payNow",
                  "Pay now"
                )}

              </label>

              {/* PAY LATER */}

              <label
                style={{
                  display: "block",
                  marginBottom: "12px",
                  cursor: "pointer",
                }}
              >

                <input
                  type="radio"
                  name="paymentMethod"
                  value="paylater"
                  checked={
                    paymentMethod ===
                    "paylater"
                  }
                  onChange={(event) =>
                    setPaymentMethod(
                      event.target.value
                    )
                  }
                  style={{
                    marginRight: "10px",
                  }}
                />

                {getTranslation(
                  "payLater",
                  "Pay later"
                )}

              </label>

            </div>

          </div>

          {/* ==================================================
              TERMS
          ================================================== */}

          <div
            className="car-box price-box"
            style={{
              padding: "20px",
              marginBottom: "20px",
            }}
          >

            <label
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "10px",
                cursor: "pointer",
              }}
            >

              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(event) =>
                  setAcceptedTerms(
                    event.target.checked
                  )
                }
                style={{
                  marginTop: "4px",
                  width: "16px",
                  height: "16px",
                }}
              />

              <span>
                {getTranslation(
                  "acceptTerms",
                  "I accept the terms and conditions."
                )}
              </span>

            </label>

          </div>

          {/* ==================================================
              FINAL TOTAL
          ================================================== */}

          <div
            className="price-box"
            style={{
              padding: "20px",
              marginBottom: "20px",
              textAlign: "right",
              background: "#edf7ec",
            }}
          >

            <span
              style={{
                fontSize: "18px",
                fontWeight: "600",
              }}
            >
              {getTranslation(
                "finalTotal",
                "Final Total"
              )}
            </span>

            {" "}

            <strong
              className="estimatedtotalprice"
              style={{
                fontSize: "24px",
              }}
            >
              {finalTotal.toFixed(2)} €
            </strong>

          </div>

          {/* ==================================================
              BUTTONS
          ================================================== */}

          <div
            style={{
              paddingRight: "2px",
              width: "auto",
              height: "auto",
              float: "right",
              marginRight: "1px",
              marginBottom: "30px",
            }}
          >

            <button
              type="button"
              className="nextbutton rightbut"
              onClick={handlePrevious}
              style={{
                border: "none",
                cursor: "pointer",
                marginRight: "5px",
              }}
            >
              {getTranslation(
                "goPrevious",
                "Go Previous"
              )}
            </button>

            <button
              type="button"
              className="nextbutton1 nextbutton"
              onClick={
                handleConfirmBooking
              }
              style={{
                border: "none",
                cursor: "pointer",
              }}
            >
              {getTranslation(
                "confirmBooking",
                "Confirm Booking »"
              )}
            </button>

          </div>

          <div className="clearfix"></div>

        </div>

        <div className="clearfix"></div>

      </div>

    </div>
  );
}

export default BookACarFour;